import { mapApiPaginated } from "@eudonet/opal";
import { isDonationStatus } from "../../domain/entities/Donation";
import { logger } from "@/infrastructure/services/LoggerService";
import { searchDonationsPage, getDonation, createDonation, updateDonation, deleteDonation, getDonationCatalogs, listDonationComments, listDonationHistory, createDonationComment, updateDonationComment, deleteDonationComment, getAvailableDonationTransitions, } from "../api/donationApi";
import { mapApiDonationToDonation } from "../mappers/donationMapper";
export async function getDonations(options) {
    const { body: apiResponse, searchedByServer } = await searchDonationsPage({
        q: options?.q,
        status: options?.status,
        paymentMethod: options?.paymentMethod,
        donationType: options?.donationType,
        statuses: options?.statuses,
        paymentMethods: options?.paymentMethods,
        donationTypes: options?.donationTypes,
        sources: options?.sources,
        withoutSource: options?.withoutSource,
        donorId: options?.donorId,
        contactId: options?.contactId,
        organizationId: options?.organizationId,
        campaignId: options?.campaignId,
        dateFrom: options?.dateFrom,
        dateTo: options?.dateTo,
        page: options?.page,
        pageSize: options?.pageSize,
        sortBy: options?.sortBy,
        sortOrder: options?.sortOrder,
        allowSearchIndex: options?.allowSearchIndex,
    });
    // `searchedByServer` tells the donations list whether `q` was matched server-side (OPSEARCH) or
    // whether it still has to filter the loaded page itself (OPDON matches the label only).
    return { ...mapApiPaginated(apiResponse, mapApiDonationToDonation), searchedByServer };
}
export async function fetchDonation(donationId) {
    const apiDonation = await getDonation(donationId);
    return mapApiDonationToDonation(apiDonation);
}
export async function saveDonation(payload) {
    const apiDonation = await createDonation(payload);
    return mapApiDonationToDonation(apiDonation);
}
export async function editDonation(donationId, payload) {
    const apiDonation = await updateDonation(donationId, payload);
    return mapApiDonationToDonation(apiDonation);
}
export async function removeDonation(donationId) {
    await deleteDonation(donationId);
}
export async function fetchDonationCatalogs() {
    return getDonationCatalogs();
}
export async function fetchAvailableDonationTransitions(donationId) {
    const transitions = await getAvailableDonationTransitions(donationId);
    // Guards the closed-set guarantee for THIS list specifically — donation TRANSITIONS arrive as a
    // raw string[] with no interface-level typing, unlike `ApiDonation.transactionStatus`, which the
    // mapper trusts as DonationStatus by the same convention as donationType/paymentMethod. A status
    // OPDON adds/renames before this union catches up would otherwise flow straight into the
    // transitions half of the Select's options, with no theme/icon entry and a raw untranslated
    // i18n key.
    return transitions.filter((status) => {
        if (isDonationStatus(status))
            return true;
        logger.error("fetchAvailableDonationTransitions: unknown donation status from backend", status);
        return false;
    });
}
// ─── Donation comments (OP-5091) ───────────────────────────────────────────────
export async function fetchDonationComments(donationId, page = 1, perPage = 50) {
    return listDonationComments(donationId, page, perPage);
}
export async function addDonationComment(donationId, content) {
    return createDonationComment(donationId, content);
}
export async function editDonationComment(commentId, content) {
    return updateDonationComment(commentId, content);
}
export async function removeDonationComment(commentId) {
    return deleteDonationComment(commentId);
}
// ─── Donation change history (OP-5075) ──────────────────────────────────────────
const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/**
 * Derives the displayed "Source" from the raw `modified_by` actor. A bare Keycloak
 * user id means a portal edit; `system:<source>` background writes map to the
 * matching source. Unknown values fall back to the user-interface source.
 */
function deriveSource(modifiedBy) {
    if (GUID_PATTERN.test(modifiedBy))
        return "UserInterface";
    const source = modifiedBy.toLowerCase().startsWith("system:")
        ? modifiedBy.slice("system:".length).toLowerCase()
        : modifiedBy.toLowerCase();
    switch (source) {
        case "ai":
            return "Ai";
        case "import":
            return "Import";
        case "repository":
        case "referentiel":
            return "Repository";
        case "form":
        case "formulaire":
            return "Form";
        default:
            return "UserInterface";
    }
}
/** Page size used when pulling the full audit history before client-side paging. */
const HISTORY_PAGE_SIZE = 100;
/** Safety bound on the number of pages fetched (100 * 50 = 5000 entries). */
const HISTORY_MAX_PAGES = 50;
/**
 * Pulls every audit entry for a donation by paging through the BFF endpoint until a
 * short (or empty) page is returned, so a donation with more than one page of history
 * is not silently truncated. Bounded by {@link HISTORY_MAX_PAGES} as a safety net.
 */
async function fetchAllHistoryEntries(donationId) {
    const all = [];
    for (let page = 1; page <= HISTORY_MAX_PAGES; page++) {
        const batch = await listDonationHistory(donationId, page, HISTORY_PAGE_SIZE);
        all.push(...batch);
        if (batch.length < HISTORY_PAGE_SIZE)
            break;
    }
    return all;
}
/**
 * Fetches a donation's full change history (donation + comments + tax receipts) and
 * maps each audit entry to one or more {@link ChangeHistoryEntry} rows:
 *
 * - Donation Updated → one row per changed field (field-level granularity).
 * - All Created / Deleted events and non-Donation entity types → one summary
 *   row per audit entry. The renderer uses `operation` + `entityType` to build
 *   the localized action label; `field` and `value` are empty.
 */
export async function fetchDonationHistory(donationId) {
    const entries = await fetchAllHistoryEntries(donationId);
    return entries.flatMap((entry) => {
        const source = deriveSource(entry.modifiedBy);
        const author = entry.modifiedByDisplayName?.trim() || undefined;
        const operation = entry.operation;
        const entityType = entry.entityType;
        // Donation field-level diffs: one row per changed field so the user can see
        // exactly what was modified. Created/Deleted events have no useful field diff.
        if (entityType === "Donation" && operation === "Updated") {
            return entry.changes.map((change) => ({
                id: `${entry.auditId}:${change.field}`,
                operation,
                entityType,
                field: change.field,
                value: change.newValue ?? "",
                previousValue: change.oldValue ?? "",
                date: entry.modifiedAt,
                source,
                author,
            }));
        }
        // DonationComment and TaxReceipt lifecycle events always produce a summary row.
        // Donation Created/Deleted with no field-level diff (e.g. soft-delete audit rows)
        // are omitted — same as the pre-OP-5844 flatMap behavior.
        if (entityType === "Donation" && entry.changes.length === 0)
            return [];
        return [
            {
                id: entry.auditId,
                operation,
                entityType,
                field: "",
                value: "",
                date: entry.modifiedAt,
                source,
                author,
            },
        ];
    });
}
